import { useStudentParent } from '../../api/hooks';
import { shortDay } from './digestHelpers';

export type PreviewChannel = 'telegram' | 'email';

const MS = 'mb-[3px] font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted';

/**
 * The phone mock of what a parent receives (design `.phone`). Figures come from the student's
 * latest generated digest; before one exists the mock says so instead of inventing numbers.
 */
export default function DigestPreview({ studentId, name, channel, parentEmail }: {
  studentId: number | null; name: string; channel: PreviewChannel; parentEmail: string;
}) {
  const parent = useStudentParent(studentId ?? 0);
  const digest = parent.data?.latest_digest ?? null;
  const p = digest?.payload ?? null;
  const first = name.split(' ')[0];
  const areas = p ? p.areas_to_support : [];
  const exams = p?.upcoming_exams ?? [];
  const week = digest ? shortDay(digest.week_start).replace(/^\w+ /, '') : '';
  const tg = channel === 'telegram';

  return (
    <div role="figure" aria-label={`Preview of the digest ${name}’s parent receives`}
      className="mx-auto w-[300px] max-w-full rounded-[40px] bg-navy p-3 shadow-card-hover">
      <div className="flex min-h-[540px] flex-col overflow-hidden rounded-[30px] bg-line-strong">
        <div aria-hidden="true" className="flex justify-between px-5 pb-1.5 pt-2.5 font-mono text-[11px] font-semibold text-ink"><span>9:41</span><span>4G</span></div>
        {tg ? (
          <div className="flex items-center gap-2.5 border-b border-line bg-surface px-3.5 pb-2.5 pt-2">
            <span aria-hidden="true" className="grid h-[30px] w-[30px] place-items-center rounded-[9px] bg-navy font-display text-sm font-bold text-white">Y</span>
            <span><span className="block text-[13px] font-semibold">YADA School</span><span className="text-[11px] text-muted">bot</span></span>
          </div>
        ) : (
          <div className="flex flex-col items-start gap-0.5 border-b border-line bg-surface px-3.5 pb-2.5 pt-2">
            <span className="text-[11px] text-muted">From: YADA School</span>
            <span className="text-[13px] font-semibold">Weekly update for {first}{week ? ` · week of ${week}` : ''}</span>
            <span className="text-[11px] text-muted">To: {parentEmail || 'the parent'}</span>
          </div>
        )}
        <div className={`flex flex-col gap-2.5 bg-surface p-3.5 text-[13px] leading-normal ${tg ? 'm-3 rounded-[18px] rounded-bl-[6px]' : 'flex-1 rounded-[12px]'}`}>
          <div className="font-display text-[15px] font-bold">{name} · this week</div>
          {parent.isError ? (
            <p role="alert" className="text-danger-ink">Could not load the latest digest for {first}.</p>
          ) : !p ? (
            <p className="text-muted">{parent.isLoading ? 'Loading the latest digest…' : `No digest has been generated for ${first} yet.`}</p>
          ) : (
            <>
              <div>
                <div className={MS}>Hours studied</div>
                <span className="font-mono text-xl font-semibold">{p.hours_studied} h</span>
              </div>
              {p.strongest_subject && <div><div className={MS}>Strongest subject</div>{p.strongest_subject}</div>}
              {areas.length > 0 && (
                <div>
                  <div className={MS}>Areas to support</div>
                  {areas.map((a) => (
                    <div key={`${a.subject}-${a.chapter}`} className="flex justify-between gap-2">
                      <span>{a.chapter} {a.subject && <span className="text-muted">· {a.subject}</span>}</span>
                      <span className="font-mono text-danger-ink">{a.mastery_pct}%</span>
                    </div>
                  ))}
                </div>
              )}
              {exams.length > 0 && (
                <div>
                  <div className={MS}>Upcoming exams</div>
                  {exams.map((x) => (
                    <div key={`${x.name}-${x.due_date}`} className="flex justify-between gap-2">
                      <span>{x.name}</span><span className="font-mono text-muted">{shortDay(x.due_date)}</span>
                    </div>
                  ))}
                </div>
              )}
              {(p.encouragement_am || p.encouragement_en) && (
                <div className="rounded-[12px] bg-ground p-2.5">
                  {p.encouragement_am && <div lang="am" className="font-ethiopic">{p.encouragement_am}</div>}
                  {p.encouragement_en && <div className="mt-1 text-muted">{p.encouragement_en}</div>}
                </div>
              )}
            </>
          )}
          <span className="text-[11px] text-muted">You are receiving this because you opted in to YADA's weekly parent digest. Unsubscribe here.</span>
        </div>
      </div>
    </div>
  );
}
